import React, { useEffect, useState, useRef } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useGSAP } from '@gsap/react'
import * as st from '@assets/styl/differential.module.styl'
import star from '@assets/svg/star-black.svg'
import cn from 'classnames'
import 'swiper/css'
import { Swiper, SwiperSlide } from 'swiper/react'

import { useLanguage } from '../hooks'
import { graphql, useStaticQuery } from 'gatsby'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'

gsap.registerPlugin(ScrollTrigger)

const DifferentialScrollSection = () => {
  const { t } = useLanguage()
  const sectionRef = useRef<HTMLDivElement | null>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const [isMobile, setIsMobile] = useState(false)
  const swiperRef = useRef<any>(null)
  const tl = useRef<gsap.core.Timeline | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    const signal = controller.signal

    const handleResize = () => setIsMobile(window.innerWidth <= 1199)

    window.addEventListener('resize', handleResize, { signal })
    handleResize()

    return () => controller.abort()
  }, [])

  const items = [
    {
      image: 'differential-scroll-01',
      number: '01',
      title: t('Differential.modeling.1'),
      text: t('Differential.modelingDescription.1'),
    },
    {
      image: 'differential-scroll-02',
      number: '02',
      title: t('Differential.usedTech'),
      text: t('Differential.usedTechDescription.1'),
    },
    {
      image: 'differential-scroll-03',
      number: '03',
      title: (
        <>
          {t('Differential.differentialWork.1')}
          <br />
          {t('Differential.differentialWork.2')}
        </>
      ),
      text: t('Differential.differentialWorkDescription.1'),
    },
    {
      image: 'differential-scroll-04',
      number: '04',
      title: (
        <>
          {t('Differential.differentialWorkSell.1')}
          <br />
          {t('Differential.differentialWorkSell.2')}
        </>
      ),
      text: t('Differential.differentialWorkSellDescription.1'),
    },
  ]

  const data = useStaticQuery(graphql`
    query {
      allFile(
        filter: {
          name: {
            in: [
              "differential-scroll-01"
              "differential-scroll-02"
              "differential-scroll-03"
              "differential-scroll-04"
            ]
          }
        }
      ) {
        nodes {
          name
          childImageSharp {
            gatsbyImageData(placeholder: BLURRED)
          }
        }
      }
    }
  `)

  const imageMap = {}
  data.allFile.nodes.forEach((node) => {
    imageMap[node.name] = getImage(node)
  })

  useGSAP(
    () => {
      if (isMobile) return

      if (!sectionRef.current || !scrollRef.current) return

      const startValue = window.innerWidth < 1600 ? '10% top' : 'top top'

      tl.current = gsap.timeline({
        scrollTrigger: {
          trigger: sectionRef.current,
          pin: true,
          start: startValue,
          scrub: 0.8,
          anticipatePin: 1,
          pinnedContainer: sectionRef.current,
          end: () => {
            if (!scrollRef.current) return '+=0'
            const horizontalScrollWidth = scrollRef.current.scrollWidth
            const visibleWidth = scrollRef.current.offsetWidth
            const paddingOffset = 120 * 2
            return `+=${horizontalScrollWidth - visibleWidth + paddingOffset}`
          },
          invalidateOnRefresh: true,
        },
      })

      tl.current.to(scrollRef.current, {
        x: () => {
          if (!scrollRef.current) return 0
          const horizontalScrollWidth = scrollRef.current.scrollWidth
          const visibleWidth = scrollRef.current.offsetWidth
          const paddingOffset = 120 * 2
          return -(horizontalScrollWidth - visibleWidth + paddingOffset)
        },
        ease: 'none',
      })
    },
    { dependencies: [isMobile], scope: sectionRef }
  )

  return (
    <>
      <section ref={sectionRef} className={st.sectionContainer}>
        {isMobile ? (
          <>
            <div className={st.sectionTitle}>
              <img
                src={star}
                alt="Imagem do asterisco"
                width="82"
                height="82"
              />
              <h2>
                {t('Differential.title.1')}
                <br />
                <b>{t('Differential.title.2')}</b>
                <br />
                <b>{t('Differential.title.3')}</b>
                <br />
                {t('Differential.title.4')}
              </h2>
            </div>
            <Swiper
              ref={swiperRef}
              breakpoints={{
                320: {
                  slidesPerView: 1.2,
                },
                767: {
                  slidesPerView: 2.2,
                },
              }}
              className={st.slider}
            >
              {items.map(({ image, title, number, text }, index) => {
                const isEven = index % 2 === 0
                const gatsbyImage = imageMap[image]
                return (
                  <SwiperSlide key={index}>
                    <div className={cn(st.item)}>
                      {isEven ? (
                        <>
                          <GatsbyImage
                            image={gatsbyImage}
                            alt={title}
                            className={st.imageContainer}
                          />
                          <div className={st.textContainer}>
                            <span>{number}</span>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className={st.textContainer}>
                            <span>{number}</span>
                          </div>
                          <GatsbyImage
                            image={gatsbyImage}
                            alt={title}
                            className={st.imageContainer}
                          />
                        </>
                      )}

                      <div className={st.textContainer}>
                        <h4>{title}</h4>
                        <p>{text}</p>
                      </div>
                    </div>
                  </SwiperSlide>
                )
              })}
            </Swiper>
          </>
        ) : (
          <div ref={scrollRef} className={st.scrollContainer}>
            <div className={st.sectionTitle}>
              <img
                src={star}
                alt="Imagem do asterisco"
                width="82"
                height="82"
              />
              <h2>
                {t('Differential.title.1')}
                <br />
                <b>{t('Differential.title.2')}</b>
                <br />
                <b>{t('Differential.title.3')}</b>
                <br />
                {t('Differential.title.4')}
              </h2>
            </div>
            <div className={st.sectionContent}>
              {items.map(({ image, title, number, text }, index) => {
                const isEven = index % 2 === 0
                const gatsbyImage = imageMap[image]
                return (
                  <div className={cn(st.item)} key={index}>
                    {isEven ? (
                      <>
                        <GatsbyImage
                          image={gatsbyImage}
                          alt={title}
                          className={st.imageContainer}
                        />
                        <div className={st.textContainer}>
                          <span>{number}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={st.textContainer}>
                          <span>{number}</span>
                        </div>
                        <GatsbyImage
                          image={gatsbyImage}
                          alt={title}
                          className={st.imageContainer}
                        />
                      </>
                    )}

                    <div className={st.textContainer}>
                      <h4>{title}</h4>
                      <p>{text}</p>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </section>
    </>
  )
}

export default DifferentialScrollSection
